import type { Account, RestoreAccount } from '@repo/api-gw-sdk';

import type { FieldValidation } from '@/components/form/validation/fieldValidation';
import { getAccountRoleArn, getRestoreNodeRoleArn } from '@/utils/accounts';

/** Matches API gateway validation in create_backup_vault_impl.go */
const MRAP_ALIAS_PATTERN = /^[a-z0-9]+\.mrap$/;

export const mrapAliasFieldValidation: FieldValidation = {
  validateInput: (value: string) => {
    const trimmed = value.trim();
    return trimmed === '' || MRAP_ALIAS_PATTERN.test(trimmed);
  },
  errorMessage: () =>
    'Use the auto-generated alias ending in .mrap (e.g. mfzwi23gnjvgw.mrap)',
};

export const getByovPolicyJson = (
  bucketName: string,
  scanningAccountIds: string[],
  dlsgFrontend: boolean,
  restoreAccounts?: RestoreAccount[]
) => {
  // EonScanningNodeRole is the principal that writes backups to the vault, and
  // under multi-scanning every connected scanning account (primary + non-primary)
  // runs backups, so each one needs a grant. Fall back to a placeholder when no
  // scanning account is connected, so the copyable policy reads as a template
  // rather than an invalid empty principal.
  const accountIds =
    scanningAccountIds.length > 0
      ? scanningAccountIds
      : ['<scanning-account-id>'];
  const scanningNodeArns = accountIds.map(
    (id) => `arn:aws:iam::${id}:role/EonScanningNodeRole`
  );
  const gatewayArns: string[] = [];
  // The DLSG / s3-proxy gateway is provisioned only on the primary scanning
  // account (created first; the caller sorts by creation time), so its roles are
  // granted for that account alone — the gateway does not run on non-primary
  // accounts, and granting it there would reference absent principals.
  // (S3ProxyServiceRole is kept for backwards compatibility; the proxy itself
  // now runs as EonScanningNodeRole.)
  if (dlsgFrontend) {
    gatewayArns.push(
      `arn:aws:iam::${accountIds[0]}:role/S3ProxyServiceRole`,
      `arn:aws:iam::${accountIds[0]}:role/eon-gw-data-access-role`
    );
  }
  const scanningAccountRoles =
    scanningNodeArns.length === 1
      ? `"${scanningNodeArns[0]}"`
      : `[
    ${scanningNodeArns.map((arn) => `"${arn}"`).join(',\n    ')}
  ]`;
  return `
  {
    "Version": "2012-10-17",
    "Statement": [
      {
          "Sid": "AllowScanningAccess",
          "Effect": "Allow",
          "Principal": {
              "AWS": ${scanningAccountRoles}
          },
          "Action": [
              "s3:GetObject",
              "s3:PutObject",
              "s3:DeleteObject",
              "s3:GetObjectRetention",
              "s3:PutObjectRetention",
              "s3:AbortMultipartUpload",
              "s3:ListBucket"
          ],
          "Resource": [
              "arn:aws:s3:::${bucketName}",
              "arn:aws:s3:::${bucketName}/*"
          ]
      }${
        gatewayArns.length
          ? `,
      {
          "Sid": "AllowGatewayAccess",
          "Effect": "Allow",
          "Principal": {
              "AWS": ${JSON.stringify(gatewayArns)}
          },
          "Action": ["s3:GetObject", "s3:PutObject", "s3:DeleteObject", "s3:ListBucket"],
          "Resource": ["arn:aws:s3:::${bucketName}", "arn:aws:s3:::${bucketName}/*"]
      }`
          : ''
      }${restoreAccounts?.length ? getRestoreAccountsCode(restoreAccounts, bucketName) : ''}
    ]
}`;
};

const getRestoreAccountsCode = (
  restoreAccounts: RestoreAccount[],
  bucketName: string
) => {
  const nodeRolePrincipals = [
    ...new Set(restoreAccounts.map(getRestoreNodeRoleArn)),
  ];
  return `,
      {
          "Sid": "AllowRestoreAccess",
          "Effect": "Allow",
          "Principal": {
                  "AWS": [${nodeRolePrincipals.map((arn) => `"${arn}"`).join(',')}]
          },
          "Action": [
              "s3:GetObject",
              "s3:ListBucket"
          ],
          "Resource": [
              "arn:aws:s3:::${bucketName}",
              "arn:aws:s3:::${bucketName}/*"
          ]
      },
      {
          "Sid": "AthenaGetSchemaJson",
          "Effect": "Allow",
          "Principal": {
              "AWS": [${restoreAccounts.map((account) => `"${getAccountRoleArn(account)}"`).join(',')}]
          },
          "Action": "s3:GetObject",
          "Resource": [
              "arn:aws:s3:::${bucketName}/dynamodb/*/eon-*/*/*/_schema.json",
              "arn:aws:s3:::${bucketName}/db-export/*/eon-*/*/*/_schema.json",
              "arn:aws:s3:::${bucketName}/*/db-export/*/eon-*/*/*/_schema.json",
              "arn:aws:s3:::${bucketName}/*vault-v2/resources/*/snapshots/*/snapshot.json",
              "arn:aws:s3:::${bucketName}/*vault-v2/resources/*/restore-manifests/*/_done"
          ]
      },
      {
          "Sid": "AthenaQueryPolicy",
          "Effect": "Allow",
          "Principal": {
              "AWS": [${restoreAccounts.map((account) => `"${getAccountRoleArn(account)}"`).join(',')}]
          },
          "Action": [
              "s3:GetObject",
              "s3:ListBucket"
          ],
          "Resource": [
              "arn:aws:s3:::${bucketName}",
              "arn:aws:s3:::${bucketName}/*"
          ],
          "Condition": {
              "ForAllValues:StringEquals": {
                  "aws:CalledVia": "athena.amazonaws.com"
              }
          }
      }`;
};

export const getMrapPolicyJson = (
  mrapAlias: string,
  vaultAccount: Account | undefined,
  restoreAccounts: RestoreAccount[]
) => {
  const vaultAccountId =
    vaultAccount?.providerAccountId ?? '<vault-account-id>';
  const mrapArn = `arn:aws:s3::${vaultAccountId}:accesspoint/${mrapAlias}`;

  const principals = [...new Set(restoreAccounts.map(getRestoreNodeRoleArn))];

  const principalValue =
    principals.length === 0
      ? '"<restore-account-role-arn>"'
      : principals.length === 1
        ? `"${principals[0]}"`
        : `[${principals.map((arn) => `"${arn}"`).join(', ')}]`;

  return `{
    "Version": "2012-10-17",
    "Statement": [
        {
            "Sid": "AllowGetObjectFromRestoreAccount",
            "Effect": "Allow",
            "Principal": {
                "AWS": ${principalValue}
            },
            "Action": [
                "s3:GetObject"
            ],
            "Resource": [
                "${mrapArn}/object/*"
            ]
        }
    ]
}`;
};
